/* eslint-disable react/jsx-no-comment-textnodes */
import React, { useRef, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./TeamSection.css";

gsap.registerPlugin(ScrollTrigger);

// Static team member data (keys for translation lookup)
const TEAM_MEMBERS_STATIC = [
  {
    key: "albert",
    name: "Albert Kaminski",
    role: "Fullstack, Cyber Security & DevOps",
    image: "/Team_Section_Albert.jpeg",
    details: {
      location: "Berlin, Germany",
      age: "33 Years",
      experience: "CoServices, Innovence IT, PYURE",
      tools: "Docker, Django, GO",
      skills: "TypeScript, Node.js, Python",
    },
  },
  {
    key: "dominik",
    name: "Dominik Domonell",
    role: "Designer & Frontend Developer",
    image: "/Team_Section_Dominik.jpeg",
    details: {
      location: "Berlin, Germany",
      age: "38 Years",
      experience: "InfoSys, WORKEY, SysDesign",
      tools: "React, Figma, Blender",
      skills: "UI/UX, TypeScript, Three.js",
    },
  },
  {
    key: "sahiram",
    name: "Sahiram Ravikumar",
    role: "Frontend Developer & UI/UX Expert",
    image: "/Team_Section_Sahiram.jpeg",
    details: {
      location: "Berlin, Germany",
      age: "28 Years",
      experience: "CoServices, Innovence IT",
      tools: "React, XCode, Django",
      skills: "TypeScript, Java , SwiftUI",
    },
  },
];

const useSpotlightEffect = (setCursorHover) => {
  const elementRef = useRef(null);

  const handleMouseEnter = () => {
    if (window.innerWidth <= 768) return;
    if (setCursorHover) setCursorHover(true);
    if (elementRef.current) {
      elementRef.current.classList.add("spotlight-active");
      gsap.to(elementRef.current, {
        "--spotlight-size": "75px",
        scale: 1.02,
        duration: 0.3,
        ease: "power1.out",
      });
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth <= 768) return;
    if (setCursorHover) setCursorHover(false);
    if (elementRef.current) {
      elementRef.current.classList.remove("spotlight-active");
      gsap.to(elementRef.current, {
        "--spotlight-size": "0px",
        scale: 1,
        duration: 0.3,
        ease: "power1.out",
      });
    }
  };

  const handleMouseMove = (e) => {
    if (window.innerWidth <= 768) return;
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const scaleX = gsap.getProperty(elementRef.current, "scaleX") || 1;
      const scaleY = gsap.getProperty(elementRef.current, "scaleY") || 1;

      elementRef.current.style.setProperty("--cursor-x", `${x / scaleX}px`);
      elementRef.current.style.setProperty("--cursor-y", `${y / scaleY}px`);
    }
  };

  return { elementRef, handleMouseEnter, handleMouseLeave, handleMouseMove };
};

const TeamMemberCard = ({ member, setCursorHover, setImageHover, t }) => {
  const containerRef = useRef(null);
  const quoteRef = useRef(null);
  const circleRef = useRef(null);
  const circleInnerRef = useRef(null);
  const detailsRef = useRef(null);
  const imageRef = useRef(null);
  const imageInnerRef = useRef(null);
  const expandedDetailsRef = useRef(null);
  const mobileDetailsRef = useRef(null);
  const mobileSlashesRef = useRef(null);
  const mobileQuoteTextRef = useRef(null);
  const hoverTweenRef = useRef(null); // Store hover-specific tweens
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  // Hook for the merged Name/Role block
  const {
    elementRef: infoRef,
    handleMouseEnter: handleInfoEnter,
    handleMouseLeave: handleInfoLeave,
    handleMouseMove: handleInfoMove,
  } = useSpotlightEffect(setCursorHover);

  useLayoutEffect(() => {
    const isMobile = window.innerWidth <= 768;

    const ctx = gsap.context(() => {
      const chars = quoteRef.current.querySelectorAll(".char");
      const paralaxForDetailsFrom = isMobile ? -20 : -20;
      const paralaxForDetailsTo = isMobile ? 40 : 80;

      // Text reveal with opacity and slight upward float
      gsap.fromTo(
        chars,
        {
          opacity: 0.2,
          y: 20,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.05,
          scrollTrigger: {
            trigger: quoteRef.current,
            start: "top bottom",
            end: "top 90%",
            scrub: 1,
          },
        },
      );

      // Parallax effect for the quote text
      gsap.fromTo(
        quoteRef.current,
        { y: -40 },
        {
          y: 40,
          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );

      // Parallax effect for the details (slightly different speed)
      gsap.fromTo(
        detailsRef.current,
        { y: paralaxForDetailsFrom },
        {
          y: paralaxForDetailsTo,
          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );

      // Slide in animation for name and role
      const infoBlock = detailsRef.current.querySelector(".member-info-block");
      if (infoBlock) {
        gsap.fromTo(
          infoBlock,
          {
            y: 50,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: infoBlock,
              start: "top 95%",
              end: "top 60%",
              scrub: false,
              toggleActions: "play none none reverse",
            },
          },
        );
      }

      // Parallax effect for the background circle
      gsap.fromTo(
        circleRef.current,
        { yPercent: -5 },
        {
          yPercent: 5,
          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );

      // Parallax effect for the image
      gsap.fromTo(
        imageRef.current,
        { yPercent: -10 },
        {
          yPercent: 10,
          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );

      // Synchronized fly-in animation using Timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%",
          end: "top 50%",
          toggleActions: "play none none reverse",
        },
      });

      tl.fromTo(
        circleRef.current,
        {
          y: 300,
          opacity: 0,
          rotation: -45,
          scale: 0.5,
        },
        {
          y: 0,
          opacity: 1,
          rotation: 0,
          scale: 1,
          duration: 0.8,
          ease: "power4.out",
        },
      ).fromTo(
        imageRef.current,
        {
          y: 300,
          opacity: 0,
          rotation: -90,
          scale: 0.5,
        },
        {
          y: 0,
          opacity: 1,
          rotation: 0,
          scale: 1,
          duration: 0.8,
          ease: "power4.out",
        },
        "<",
      );

      // Mobile quote animations - Slashes from Left, Text from Right
      if (isMobile) {
        if (mobileSlashesRef.current) {
          gsap.fromTo(
            mobileSlashesRef.current,
            { x: -50, opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.8,
              ease: "power2.out",
              scrollTrigger: {
                trigger: mobileSlashesRef.current,
                start: "top 95%",
                end: "top 80%",
                toggleActions: "play none none reverse",
              },
            },
          );
        }

        if (mobileQuoteTextRef.current) {
          gsap.fromTo(
            mobileQuoteTextRef.current,
            { x: 50, opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.8,
              ease: "power2.out",
              scrollTrigger: {
                trigger: mobileQuoteTextRef.current,
                start: "top 95%",
                end: "top 80%",
                toggleActions: "play none none reverse",
              },
            },
          );
        }
      }
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const handleTextEnter = () => {
    if (window.innerWidth <= 768) return;
    setCursorHover(true);
    quoteRef.current.classList.add("spotlight-active");
    gsap.to(quoteRef.current, {
      "--spotlight-size": "75px",
      scale: 1.02,
      duration: 0.3,
      ease: "power1.out",
    });
  };

  const handleTextLeave = () => {
    if (window.innerWidth <= 768) return;
    setCursorHover(false);
    quoteRef.current.classList.remove("spotlight-active");
    gsap.to(quoteRef.current, {
      "--spotlight-size": "0px",
      scale: 1,
      duration: 0.3,
      ease: "power1.out",
    });
  };

  const handleMouseMove = (e) => {
    if (window.innerWidth <= 768) return;
    const rect = quoteRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const scaleX = gsap.getProperty(quoteRef.current, "scaleX") || 1;
    const scaleY = gsap.getProperty(quoteRef.current, "scaleY") || 1;

    quoteRef.current.style.setProperty("--cursor-x", `${x / scaleX}px`);
    quoteRef.current.style.setProperty("--cursor-y", `${y / scaleY}px`);
  };

  // Image hover handlers - 3D tilt effect
  const handleImageEnter = () => {
    if (window.innerWidth <= 768) return;
    if (setImageHover) setImageHover(true);

    // Kill only hover-specific tweens (width, zIndex), not ScrollTrigger parallax
    if (hoverTweenRef.current) {
      hoverTweenRef.current.kill();
    }

    // Expand background (only width, not yPercent which is controlled by ScrollTrigger)
    gsap.set(circleRef.current, { zIndex: 10 });
    hoverTweenRef.current = gsap.to(circleRef.current, {
      width: "700px",
      duration: 0.5,
      ease: "power3.out",
    });

    // Ensure image is on top
    gsap.set(imageRef.current, { zIndex: 20 });

    // Show details - kill any pending tweens first, then set display and animate
    gsap.killTweensOf(expandedDetailsRef.current);
    gsap.set(expandedDetailsRef.current, { display: "flex" });
    gsap.to(expandedDetailsRef.current, {
      autoAlpha: 1,
      x: 0,
      duration: 0.4,
      delay: 0.1,
    });
  };

  const handleImageLeave = () => {
    if (window.innerWidth <= 768) return;
    if (setImageHover) setImageHover(false);

    // Kill only hover-specific tweens
    if (hoverTweenRef.current) {
      hoverTweenRef.current.kill();
    }
    gsap.killTweensOf(expandedDetailsRef.current);

    // Reset background width (not yPercent)
    hoverTweenRef.current = gsap.to(circleRef.current, {
      width: "100%",
      duration: 0.5,
      ease: "power3.out",
      onComplete: () => {
        gsap.set(circleRef.current, { zIndex: 0 });
        gsap.set(imageRef.current, { zIndex: 1 });
      },
    });

    // Hide details and set display none after animation
    gsap.to(expandedDetailsRef.current, {
      autoAlpha: 0,
      x: -20,
      duration: 0.3,
      onComplete: function () {
        // Only set display none if still hidden (autoAlpha is 0)
        if (gsap.getProperty(expandedDetailsRef.current, "opacity") === 0) {
          gsap.set(expandedDetailsRef.current, { display: "none" });
        }
      },
    });

    // Reset tilt on leave for both image and background
    if (imageInnerRef.current) {
      gsap.to(imageInnerRef.current, {
        rotateX: 0,
        rotateY: 0,
        duration: 0.5,
        ease: "power2.out",
      });
    }
    if (circleInnerRef.current) {
      gsap.to(circleInnerRef.current, {
        rotateX: 0,
        rotateY: 0,
        duration: 0.5,
        ease: "power2.out",
      });
    }
  };

  const handleImageMove = (e) => {
    if (window.innerWidth <= 768) return;
    if (imageRef.current) {
      // Use outer ref for rect calculation
      const rect = imageRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Calculate tilt based on mouse position (-15 to 15 degrees)
      // Clamp values to prevent extreme tilting when hovering expanded details
      let rotateY = ((x - centerX) / centerX) * 15;
      let rotateX = ((centerY - y) / centerY) * 15;

      rotateY = Math.max(-15, Math.min(15, rotateY));
      rotateX = Math.max(-15, Math.min(15, rotateX));

      // Tilt the image inner wrapper
      if (imageInnerRef.current) {
        gsap.to(imageInnerRef.current, {
          rotateX: rotateX,
          rotateY: rotateY,
          duration: 0.3,
          ease: "power2.out",
        });
      }

      // Tilt the orange background inner wrapper
      if (circleInnerRef.current) {
        gsap.to(circleInnerRef.current, {
          rotateX: rotateX,
          rotateY: rotateY,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    }
  };

  const toggleMobileDetails = () => {
    if (window.innerWidth > 768) return;

    const newState = !isMobileExpanded;
    setIsMobileExpanded(newState);

    if (mobileDetailsRef.current) {
      if (newState) {
        gsap.set(mobileDetailsRef.current, { height: "auto", display: "flex" });
        const autoHeight = mobileDetailsRef.current.scrollHeight + 40; // + padding
        gsap.fromTo(
          mobileDetailsRef.current,
          { height: 0, opacity: 0, marginTop: 0 },
          {
            height: autoHeight,
            opacity: 1,
            marginTop: 15,
            duration: 0.5,
            ease: "power3.out",
          },
        );
      } else {
        gsap.to(mobileDetailsRef.current, {
          height: 0,
          opacity: 0,
          marginTop: 0,
          duration: 0.4,
          ease: "power3.in",
          onComplete: () => {
            gsap.set(mobileDetailsRef.current, { display: "none" });
          },
        });
      }
    }
  };

  return (
    <div className="team-member-row" ref={containerRef}>
      <div
        className="member-image-wrapper"
        onMouseEnter={handleImageEnter}
        onMouseLeave={handleImageLeave}
        onMouseMove={handleImageMove}
        onClick={toggleMobileDetails}
      >
        <div className="member-image-bg" ref={circleRef}>
          <div className="member-image-bg-inner" ref={circleInnerRef}>
            <div className="member-expanded-details" ref={expandedDetailsRef}>
              <div className="detail-row">
                <span className="detail-label">
                  {t("team.labels.location")}
                </span>
                <span className="detail-value">{member.details.location}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("team.labels.age")}</span>
                <span className="detail-value">{member.details.age}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">
                  {t("team.labels.experience")}
                </span>
                <span className="detail-value">
                  {member.details.experience}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("team.labels.tools")}</span>
                <span className="detail-value">{member.details.tools}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t("team.labels.skills")}</span>
                <span className="detail-value">{member.details.skills}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="member-image-right" ref={imageRef}>
          <div className="member-image-inner" ref={imageInnerRef}>
            <img src={member.image} alt={member.name} />
          </div>
        </div>
      </div>
      <div className="member-content-left">
        <p
          className="member-quote"
          ref={quoteRef}
          onMouseEnter={handleTextEnter}
          onMouseLeave={handleTextLeave}
          onMouseMove={handleMouseMove}
        >
          <span className="quote-dot"></span>
          {member.description.split("").map((char, i) => (
            <span key={i} className="char">
              {char}
            </span>
          ))}
        </p>
        <div className="member-details" ref={detailsRef}>
          {/* Merged Name and Role into one interactive block */}
          <div
            className="member-info-block spotlight-text"
            ref={infoRef}
            onMouseEnter={handleInfoEnter}
            onMouseLeave={handleInfoLeave}
            onMouseMove={handleInfoMove}
            onClick={toggleMobileDetails}
          >
            <span className="member-name">{member.name}</span>
            <span className="member-role">
              {member.role}
              <span
                className={`mobile-expand-icon ${isMobileExpanded ? "is-expanded" : ""}`}
                style={{ marginLeft: "auto" }}
              >
                <svg
                  width="12"
                  height="8"
                  viewBox="0 0 12 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L6 6L11 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </span>
          </div>

          {/* Mobile-only details card */}
          <div
            className={`member-mobile-details ${isMobileExpanded ? "is-expanded" : ""}`}
            ref={mobileDetailsRef}
            style={{
              display: "none",
              height: 0,
              opacity: 0,
              overflow: "hidden",
            }}
          >
            <div className="detail-row">
              <span className="detail-label">{t("team.labels.location")}</span>
              <span className="detail-value">{member.details.location}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">
                {t("team.labels.experience")}
              </span>
              <span className="detail-value">{member.details.experience}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t("team.labels.tools")}</span>
              <span className="detail-value">{member.details.tools}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t("team.labels.skills")}</span>
              <span className="detail-value">{member.details.skills}</span>
            </div>
          </div>
          <div className="member-mobile-quote-container">
            <span className="mobile-quote-slashes" ref={mobileSlashesRef}>
              //
            </span>
            <p className="member-mobile-quote-text" ref={mobileQuoteTextRef}>
              {member.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const TeamSection = () => {
  const { t } = useTranslation();

  // Build TEAM_MEMBERS with translated descriptions
  const TEAM_MEMBERS = TEAM_MEMBERS_STATIC.map((member) => ({
    ...member,
    description: t(`team.members.${member.key}.description`),
  }));

  const sectionRef = useRef(null);
  const cursorRef = useRef(null);
  const headerContainerRef = useRef(null);
  const subheadlineRef = useRef(null);
  const headlineRef = useRef(null);
  const [isHoveringText, setIsHoveringText] = useState(false);
  const [isHoveringImage, setIsHoveringImage] = useState(false);

  // Custom hook for header block (affects both children)
  const subheadlineSpotlightRef = useRef(null);

  const handleSubheadlineEnter = () => {
    setIsHoveringText(true);
    const container = subheadlineSpotlightRef.current;
    if (container) {
      const children = container.querySelectorAll(
        ".team-subheadline, .team-header-title",
      );
      children.forEach((child) => {
        gsap.to(child, {
          "--spotlight-size": "75px",
          scale: 1.02,
          duration: 0.3,
          ease: "power1.out",
        });
      });
    }
  };

  const handleSubheadlineLeave = () => {
    setIsHoveringText(false);
    const container = subheadlineSpotlightRef.current;
    if (container) {
      const children = container.querySelectorAll(
        ".team-subheadline, .team-header-title",
      );
      children.forEach((child) => {
        gsap.to(child, {
          "--spotlight-size": "0px",
          scale: 1,
          duration: 0.3,
          ease: "power1.out",
        });
      });
    }
  };

  const handleSubheadlineMove = (e) => {
    const container = subheadlineSpotlightRef.current;
    if (container) {
      const children = container.querySelectorAll(
        ".team-subheadline, .team-header-title",
      );
      children.forEach((child) => {
        const rect = child.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const scaleX = gsap.getProperty(child, "scaleX") || 1;
        const scaleY = gsap.getProperty(child, "scaleY") || 1;

        child.style.setProperty("--cursor-x", `${x / scaleX}px`);
        child.style.setProperty("--cursor-y", `${y / scaleY}px`);
      });
    }
  };

  // Hook for Description
  const {
    elementRef: descRef,
    handleMouseEnter: handleDescEnter,
    handleMouseLeave: handleDescLeave,
    handleMouseMove: handleDescMove,
  } = useSpotlightEffect(setIsHoveringText);

  const description = t("team.description");

  useLayoutEffect(() => {
    const cursor = cursorRef.current;
    const xTo = gsap.quickTo(cursor, "x", { duration: 0.2, ease: "power3" });
    const yTo = gsap.quickTo(cursor, "y", { duration: 0.2, ease: "power3" });

    const handleMouseMove = (e) => {
      xTo(e.clientX);
      yTo(e.clientY);
    };

    const section = sectionRef.current;

    const handleMouseEnter = () =>
      gsap.to(cursor, { autoAlpha: 1, duration: 0.3 });
    const handleMouseLeave = () =>
      gsap.to(cursor, { autoAlpha: 0, duration: 0.3 });

    window.addEventListener("mousemove", handleMouseMove);
    section.addEventListener("mouseenter", handleMouseEnter);
    section.addEventListener("mouseleave", handleMouseLeave);

    // Header animations
    const ctx = gsap.context(() => {
      // Headline (Our Team) slides in from left
      gsap.fromTo(
        headlineRef.current,
        { x: -100, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerContainerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        },
      );

      // Subheadline (Meet the people behind) slides in from right
      gsap.fromTo(
        subheadlineRef.current,
        { x: 100, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerContainerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        },
      );

      // Description slides in from bottom
      gsap.fromTo(
        descRef.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerContainerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        },
      );
    }, sectionRef);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      section.removeEventListener("mouseenter", handleMouseEnter);
      section.removeEventListener("mouseleave", handleMouseLeave);
      ctx.revert();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Split description into words for animation
  const descriptionWords = description.split(" ");

  return (
    <div className="team-section" ref={sectionRef}>
      <div
        className={`team-cursor ${isHoveringText ? "hovered" : ""} ${
          isHoveringImage ? "image-hovered" : ""
        }`}
        ref={cursorRef}
      ></div>
      <div className="team-header-container" ref={headerContainerRef}>
        <div className="team-header-top">
          <div
            className="team-header-left"
            ref={subheadlineSpotlightRef}
            onMouseEnter={handleSubheadlineEnter}
            onMouseLeave={handleSubheadlineLeave}
            onMouseMove={handleSubheadlineMove}
          >
            <span className="team-subheadline" ref={subheadlineRef}>
              {t("team.subheadline")}
            </span>
            <span className="team-header-title" ref={headlineRef}>
              {t("team.headline")}
            </span>
          </div>
          <p
            className="team-description spotlight-text"
            ref={descRef}
            onMouseEnter={handleDescEnter}
            onMouseLeave={handleDescLeave}
            onMouseMove={handleDescMove}
          >
            {descriptionWords.map((word, i) => (
              <span key={i} className="word">
                {word}&nbsp;
              </span>
            ))}
          </p>
        </div>
        <div className="team-header-line"></div>
      </div>

      <div className="team-list">
        {TEAM_MEMBERS.map((member, index) => (
          <TeamMemberCard
            key={index}
            member={member}
            setCursorHover={setIsHoveringText}
            setImageHover={setIsHoveringImage}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

export default TeamSection;
